import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { FormControl, FormGroup } from '@angular/forms';
import {
  IAccessProfileGetByIdResponseApi,
  IAccessProfileHasUserResponseApi,
  IAccessProfileListResponseApi,
  IAccessProfileRegister,
  IAccessProfileTransfer,
  IGetMenusResponseApi
} from 'src/app/interfaces/users/access.interface';
import { ISelectOptionApiResponse } from 'src/app/interfaces/components/select.interface';

@Injectable({
  providedIn: 'root'
})
export class AccessService {
  private readonly http = inject(HttpClient);

  formFilterList = new FormGroup({
    name: new FormControl(''),
    status: new FormControl(''),
    sortBy: new FormControl(''),
    sortOrder: new FormControl(''),
    page: new FormControl(1),
    pageSize: new FormControl(5),
  });

  listAll(payload: {
    pageSize: number;
    page: number;
    name?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: string;
  }) {
    let params = new HttpParams()
      .set('page', payload.page)
      .set('pageSize', payload.pageSize);

    if (payload.name) {
      params = params.set('name', payload.name);
    }

    if (payload.status) {
      params = params.set('status', payload.status);
    }

    if (payload.sortBy) {
      params = params.set('sortBy', payload.sortBy);
    }

    if (payload.sortOrder) {
      params = params.set('sortOrder', payload.sortOrder);
    }

    return this.http.get<IAccessProfileListResponseApi>(
      `${environment.api}/accessProfile/all`, { params }
    );
  }

  export(payload: {
    name?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: string;
  }) {
    let params = new HttpParams();

    if (payload.name) {
      params = params.set('name', payload.name);
    }

    if (payload.status) {
      params = params.set('status', payload.status);
    }

    if (payload.sortBy) {
      params = params.set('sortBy', payload.sortBy);
    }

    if (payload.sortOrder) {
      params = params.set('sortOrder', payload.sortOrder);
    }

    return this.http.get(
      `${environment.api}/accessProfile/all/export`,
      { params, responseType: 'blob' }
    );
  }

  register(body: IAccessProfileRegister) {
    if (body.id) {
     return this.update(body);
    }
    else {
     return this.create(body);
    }
   }

  create(body: IAccessProfileRegister) {
    return this.http.post<IAccessProfileGetByIdResponseApi>(
      `${environment.api}/accessProfile/create`, body
    );
   }

  update(body: IAccessProfileRegister) {
    return this.http.put<IAccessProfileGetByIdResponseApi>(
      `${environment.api}/accessProfile/update/${body.id}`, body
    );
   }

  delete(id: string) {
    return this.http.delete(`${environment.api}/accessProfile/delete/${id}`).pipe(
      (res) => res,
      (error) => error
    );
  }

  userAssociatedToAccessProfile(id: string) {
    return this.http.get<IAccessProfileHasUserResponseApi>(
      `${environment.api}/accessProfile/${id}/has-user`,
      {
        headers: new HttpHeaders().set('x-skip-loading', 'true'),
      }
    );
  }

  transferAccessProfile(payload: IAccessProfileTransfer) {
    const params = new HttpParams()
      .set('newAccessProfileId', payload.newAccessProfileId);

    return this.http.patch(
      `${environment.api}/accessProfile/${payload.id}/transfer-users`,
      null,
      { params }
    );
  }

  toggle(id: string, status: boolean) {
    return this.http.patch(`${environment.api}/accessProfile/${id}/change-status`, { status }).pipe(
      (res) => res,
      (error) => error
    );
  }

  listMenus() {
    return this.http.get<IGetMenusResponseApi>(
      `${environment.api}/accessProfile/all/menus`
    );
  }

  getById(id: string) {
    return this.http.get<any>(
      `${environment.api}/accessProfile/${id}`
    );
  }

  getAllNoPaginated(payload: {
    name?: string;
  }) {
    let params = new HttpParams()
      .set('status', 'true');

    if (payload.name) {
      params = params.set('name', payload.name);
    }

    return this.http.get<ISelectOptionApiResponse>(
      `${environment.api}/accessProfile/all/no-pagination`,
      {
        params,
        headers: new HttpHeaders().set('x-skip-loading', 'true'),
      }
    );
  }
}
